import Quill from 'quill';
import Delta from 'quill-delta';

import { AppDispatch } from 'app/store';
import { AppUiNotificationsActions } from 'app/store/AppUINotifications/AppUINotifications.slice';

import pasteImage from '../utils/image-paste';
import uploadImage from '../utils/image-upload';
import convertBase64ToFile, { isBase64 } from '../utils/convert-base64-to-file';
import downloadImageToFile from '../utils/download-image-by-url';
import { sanitizeHTMLDocument } from '../utils/html-sanitizer';

const Module = Quill.import('core/module');

interface PasteSanitizeOptions {
  dispatch: AppDispatch;
  startLoading? (): void;
  endLoading? (): void;
  removeColor?: boolean;
  removeBackground?: boolean;
  sanitizeHTML?: boolean; // Remove var tags, clean anchor attributes, remove contenteditable spans
}

class PasteSanitizeModule extends Module {
  quill: Quill;
  options: PasteSanitizeOptions;

  constructor(quill: Quill, options: PasteSanitizeOptions) {
    super(quill, options);
    this.quill = quill;
    this.options = options;

    this.handlePaste = this.handlePaste.bind(this);
    this.quill.root.addEventListener('paste', this.handlePaste, true);
  }

  destroy() {
    this.quill.root.removeEventListener('paste', this.handlePaste, true);
  }

  async handlePaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    // Check for pasted images first
    const imageFile = this.getImageFromClipboard(clipboardData);
    if (imageFile) {
      await this.handleImagePaste(imageFile);
      return;
    }

    // Process HTML or plain text
    const html = clipboardData.getData('text/html');
    const text = clipboardData.getData('text/plain');

    // If HTML is present, parse it and process nodes (e.g., images).
    // Otherwise, use Quill's default text conversion which handles newlines correctly.
    let delta: Delta;
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      await this.processHTMLNodes(doc);
      delta = this.quill.clipboard.convert({ html: doc.body.innerHTML });
    } else {
      delta = this.quill.clipboard.convert({ text: text || '' });
    }

    // Apply transformations and insert content
    delta = this.replaceInternalLinksInDelta(delta);
    delta = this.sanitizeDelta(delta);
    this.insertDelta(delta);
  }

  private getImageFromClipboard(clipboardData: DataTransfer): File | null {
    const items = Array.from(clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    return imageItem?.getAsFile() || null;
  }

  private async handleImagePaste(file: File): Promise<void> {
    await uploadImage({
      file,
      startLoading: this.options.startLoading,
      endLoading: this.options.endLoading,
      callback: (imageUrl: string) => {
        pasteImage(this.quill, imageUrl);
      }
    });
  }

  private async processHTMLNodes(doc: Document): Promise<void> {
    // First, sanitize HTML if enabled
    if (this.options.sanitizeHTML) {
      sanitizeHTMLDocument(doc);
    }

    // Then process images
    const nodes = Array.from(doc.body.childNodes);
    for (const node of nodes) {
      await this.processNodeRecursive(node);
    }
  }

  private insertDelta(delta: Delta): void {
    const range = this.quill.getSelection(true);
    this.quill.updateContents(
      new Delta().retain(range.index).delete(range.length).concat(delta),
      Quill.sources.USER
    );
    this.quill.setSelection(range.index + delta.length(), Quill.sources.SILENT);
  }

  // Recursive function to process images
  private async processNodeRecursive(node: Node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;

    // Handle images
    if (el.tagName === 'IMG') {
      await this.processImageElement(el);
    }

    // Recursively process children
    for (const child of Array.from(el.childNodes)) {
      await this.processNodeRecursive(child);
    }
  }

  private async processImageElement(el: HTMLElement): Promise<void> {
    const src = el.getAttribute('src');
    if (!src) return;

    const finalUrl = await this.getProcessedImageUrl(src);

    if (finalUrl) {
      el.setAttribute('src', finalUrl);
    } else {
      el.remove();
    }
  }

  private async getProcessedImageUrl(src: string): Promise<string | null> {
    try {
      if (isBase64(src)) {
        const file = await convertBase64ToFile(src);
        return await uploadImage({ file });
      } else if (!src.includes('cdn')) {
        const file = await downloadImageToFile(src);
        if (file) return await uploadImage({ file });
      }
      return src;
    } catch (error) {
      if (error instanceof Error) {
        this.options.dispatch(
          AppUiNotificationsActions.addSnackbar({
            message: error.message
          })
        );
      }
      return null;
    }
  }

  private replaceInternalLinksInDelta(delta: Delta): Delta {
    const editorText = this.quill.getText();
    const matches = Array.from(editorText.matchAll(/\[(\d+)\]/g));
    const numbers = matches.map(m => parseInt(m[1], 10));
    let lastNumber = numbers.length ? Math.max(...numbers) : 0;

    const newOps: Delta['ops'] = [];

    for (const op of delta.ops) {
      if (typeof op.insert === 'string') {
        // Split text by spaces to check each word
        const segments = op.insert.split(/(\s+)/);
        for (const segment of segments) {
          if (this.isInternalLink(segment)) {
            // Replace with [n] label and link
            lastNumber++;
            const label = `[${lastNumber}]`;
            newOps.push({ insert: label, attributes: { link: segment.trim() } });
          } else if (segment) {
            newOps.push({ insert: segment, attributes: op.attributes });
          }
        }
      } else {
        newOps.push(op);
      }
    }

    return new Delta({ ops: newOps });
  }

  private isInternalLink(text?: string) {
    const internalLinkRegex = /^https?:\/\/[^/]+\/admin\/cases\/(\d+)\/documents\/(\d+)\/(\d+)$/;
    return !!text?.trim().match(internalLinkRegex);
  }

  private sanitizeDelta(delta: Delta): Delta {
    const sanitizedOps = delta.ops.map(op => {
      if (!op.attributes) return op;

      const attrs = { ...op.attributes };
      if (this.options.removeColor) delete attrs.color;
      if (this.options.removeBackground) delete attrs.background;

      return { ...op, attributes: attrs };
    });

    return new Delta({ ops: sanitizedOps });
  }
}

Quill.register('modules/pasteSanitize', PasteSanitizeModule);
